import { TableProps } from "antd";
import { RefObject, useCallback, useEffect, useRef, useState } from "react";
import {
  renderTableRowCheckboxCell,
  renderTableRowCheckboxHeader,
} from "./tableRowSelectionCells";

const SCROLL_ZONE_PX = 60;

type UseTableRowDragSelectionOptions = {
  checkboxClassName?: string;
  scrollRootRef?: RefObject<HTMLElement | null>;
  isSelecting?: boolean;
  onSelectRange?: (index: number) => void;
};

function getTableScrollBody(root: HTMLElement | null): HTMLElement | null {
  if (!root) return null;
  return (
    root.querySelector<HTMLElement>(".ant-table-tbody-virtual-holder") ??
    root.querySelector<HTMLElement>(".ant-table-body") ??
    root.querySelector<HTMLElement>(".rc-virtual-list-holder") ??
    null
  );
}

function getOuterScrollContainer(el: HTMLElement | null): HTMLElement | Window {
  let parent = el?.parentElement;
  while (parent) {
    const style = window.getComputedStyle(parent);
    const overflowY = style.overflowY;
    const isScrollable =
      (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay") &&
      parent.scrollHeight > parent.clientHeight;
    if (isScrollable) {
      return parent;
    }
    parent = parent.parentElement;
  }
  return window;
}

function resolveRowIndexFromPoint<T extends { _id: string }>(
  clientX: number,
  clientY: number,
  rows: T[],
  root: HTMLElement | null
): number | null {
  if (rows.length === 0) return null;

  const el = document.elementFromPoint(clientX, clientY);
  const rowEl = el?.closest("tr");
  if (rowEl) {
    const indexAttr = rowEl.getAttribute("data-row-index");
    if (indexAttr !== null) {
      const parsed = Number(indexAttr);
      if (!Number.isNaN(parsed)) return parsed;
    }

    const checkbox = rowEl.querySelector<HTMLInputElement>(
      "input.ant-checkbox-input"
    );
    if (checkbox?.id) {
      const idx = rows.findIndex((row) => row._id === checkbox.id);
      if (idx >= 0) return idx;
    }
  }

  if (root) {
    const visibleRows = Array.from(
      root.querySelectorAll<HTMLTableRowElement>("tr[data-row-index]")
    );
    if (visibleRows.length > 0) {
      const firstRow = visibleRows[0];
      const lastRow = visibleRows[visibleRows.length - 1];

      const firstIndex = Number(firstRow.getAttribute("data-row-index"));
      const lastIndex = Number(lastRow.getAttribute("data-row-index"));

      const firstRect = firstRow.getBoundingClientRect();
      const lastRect = lastRow.getBoundingClientRect();

      if (clientY < firstRect.top) {
        return Math.max(0, firstIndex);
      }
      if (clientY > lastRect.bottom) {
        return Math.min(rows.length - 1, lastIndex);
      }

      for (const row of visibleRows) {
        const rect = row.getBoundingClientRect();
        if (clientY >= rect.top && clientY <= rect.bottom) {
          const idx = Number(row.getAttribute("data-row-index"));
          if (!Number.isNaN(idx)) return idx;
        }
      }
    }
  }

  return null;
}

export function useTableRowDragSelection<T extends { _id: string }>(
  rows: T[],
  options?: UseTableRowDragSelectionOptions
) {
  const [checkedList, setCheckedList] = useState<string[]>([]);
  const [selectedRows, setSelectedRows] = useState<T[]>([]);
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStartIndex, setSelectionStartIndex] = useState<number | null>(
    null
  );

  const activeIsSelecting = options?.isSelecting ?? isSelecting;
  const activeIsSelectingRef = useRef(activeIsSelecting);

  useEffect(() => {
    activeIsSelectingRef.current = activeIsSelecting;
  }, [activeIsSelecting]);

  const onSelectRangeRef = useRef(options?.onSelectRange);
  useEffect(() => {
    onSelectRangeRef.current = options?.onSelectRange;
  }, [options?.onSelectRange]);

  const checkedListRef = useRef(checkedList);
  const selectedRowsRef = useRef(selectedRows);
  const rowsRef = useRef(rows);
  const isSelectingRef = useRef(isSelecting);
  const selectionStartIndexRef = useRef(selectionStartIndex);

  useEffect(() => {
    checkedListRef.current = checkedList;
  }, [checkedList]);

  useEffect(() => {
    selectedRowsRef.current = selectedRows;
  }, [selectedRows]);

  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  useEffect(() => {
    isSelectingRef.current = isSelecting;
  }, [isSelecting]);

  useEffect(() => {
    selectionStartIndexRef.current = selectionStartIndex;
  }, [selectionStartIndex]);

  const clearSelection = useCallback(() => {
    setCheckedList([]);
    setSelectedRows([]);
  }, []);

  const applySelectionToIndex = useCallback((index: number) => {
    const startIndex = selectionStartIndexRef.current;
    const currentRows = rowsRef.current;

    if (
      !isSelectingRef.current ||
      startIndex === null ||
      index < 0 ||
      index >= currentRows.length
    ) {
      return;
    }

    const start = Math.min(startIndex, index);
    const end = Math.max(startIndex, index);
    const rowsToSelect = currentRows.slice(start, end + 1);
    const keysToSelect = rowsToSelect.map((row) => row._id);

    setCheckedList([
      ...checkedListRef.current.filter((key) => !keysToSelect.includes(key)),
      ...keysToSelect,
    ]);
    setSelectedRows([
      ...selectedRowsRef.current.filter(
        (item) => !rowsToSelect.some((row) => row._id === item._id)
      ),
      ...rowsToSelect,
    ]);
  }, []);

  const handleMouseDown = useCallback(
    (index: number, event: React.MouseEvent) => {
      const target = event.target as HTMLElement;
      const isCheckbox =
        target.closest(".ant-checkbox") ||
        target.closest(".ant-checkbox-wrapper") ||
        target.closest(".ant-table-selection-column");

      if (!isCheckbox || event.button !== 0) {
        return;
      }

      event.stopPropagation();

      const row = rowsRef.current[index];
      if (!row) return;

      setIsSelecting(true);
      setSelectionStartIndex(index);

      setCheckedList([
        ...checkedListRef.current.filter((key) => key !== row._id),
        row._id,
      ]);
      setSelectedRows([
        ...selectedRowsRef.current.filter((item) => item._id !== row._id),
        row,
      ]);
    },
    []
  );

  const handleMouseMove = useCallback(
    (index: number) => {
      applySelectionToIndex(index);
    },
    [applySelectionToIndex]
  );

  const handleMouseUp = useCallback((event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setIsSelecting(false);
    setSelectionStartIndex(null);
  }, []);

  useEffect(() => {
    if (!isSelecting) return;

    const onDocumentMouseUp = () => {
      setIsSelecting(false);
      setSelectionStartIndex(null);
    };

    document.addEventListener("mouseup", onDocumentMouseUp);
    return () => document.removeEventListener("mouseup", onDocumentMouseUp);
  }, [isSelecting]);

  const mousePosRef = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    if (!activeIsSelecting || !options?.scrollRootRef) {
      mousePosRef.current = null;
      return;
    }

    let animationFrameId: number | null = null;

    const onDocumentMouseMove = (event: MouseEvent) => {
      if (!activeIsSelectingRef.current) return;
      mousePosRef.current = { x: event.clientX, y: event.clientY };

      const index = resolveRowIndexFromPoint(
        event.clientX,
        event.clientY,
        rowsRef.current,
        options.scrollRootRef?.current ?? null
      );
      if (index !== null) {
        if (onSelectRangeRef.current) {
          onSelectRangeRef.current(index);
        } else {
          applySelectionToIndex(index);
        }
      }
    };

    const scrollLoop = () => {
      if (!activeIsSelectingRef.current) return;

      const mousePos = mousePosRef.current;
      const rootEl = options.scrollRootRef?.current ?? null;

      if (mousePos && rootEl) {
        const innerBody = getTableScrollBody(rootEl);
        const outerContainer = getOuterScrollContainer(rootEl);

        let didScrollInner = false;
        let didScrollOuter = false;

        if (innerBody) {
          const innerRect = innerBody.getBoundingClientRect();
          let innerSpeed = 0;

          if (mousePos.y < innerRect.top + SCROLL_ZONE_PX) {
            const dist = innerRect.top + SCROLL_ZONE_PX - mousePos.y;
            innerSpeed = -Math.min(30, Math.max(4, dist * 0.4));
          } else if (mousePos.y > innerRect.bottom - SCROLL_ZONE_PX) {
            const dist = mousePos.y - (innerRect.bottom - SCROLL_ZONE_PX);
            innerSpeed = Math.min(30, Math.max(4, dist * 0.4));
          }

          if (innerSpeed !== 0) {
            const prevScrollTop = innerBody.scrollTop;
            innerBody.scrollTop = Math.max(0, innerBody.scrollTop + innerSpeed);
            if (innerBody.scrollTop !== prevScrollTop) {
              didScrollInner = true;
              innerBody.dispatchEvent(new Event("scroll", { bubbles: true }));
            }
          }
        }

        let outerTop = 0;
        let outerBottom = window.innerHeight;

        if (outerContainer !== window) {
          const outerRect = (outerContainer as HTMLElement).getBoundingClientRect();
          outerTop = outerRect.top;
          outerBottom = outerRect.bottom;
        }

        let outerSpeed = 0;
        if (mousePos.y < outerTop + SCROLL_ZONE_PX) {
          const dist = outerTop + SCROLL_ZONE_PX - mousePos.y;
          outerSpeed = -Math.min(35, Math.max(4, dist * 0.4));
        } else if (mousePos.y > outerBottom - SCROLL_ZONE_PX) {
          const dist = mousePos.y - (outerBottom - SCROLL_ZONE_PX);
          outerSpeed = Math.min(35, Math.max(4, dist * 0.4));
        }

        if (outerSpeed !== 0) {
          if (outerContainer === window) {
            const prevY = window.scrollY;
            window.scrollBy(0, outerSpeed);
            if (window.scrollY !== prevY) {
              didScrollOuter = true;
            }
          } else {
            const el = outerContainer as HTMLElement;
            const prevTop = el.scrollTop;
            el.scrollTop = Math.max(0, el.scrollTop + outerSpeed);
            if (el.scrollTop !== prevTop) {
              didScrollOuter = true;
            }
          }
        }

        if (didScrollInner || didScrollOuter) {
          const index = resolveRowIndexFromPoint(
            mousePos.x,
            mousePos.y,
            rowsRef.current,
            rootEl
          );
          if (index !== null) {
            if (onSelectRangeRef.current) {
              onSelectRangeRef.current(index);
            } else {
              applySelectionToIndex(index);
            }
          }
        }
      }

      animationFrameId = requestAnimationFrame(scrollLoop);
    };

    document.addEventListener("mousemove", onDocumentMouseMove, {
      passive: true,
    });
    animationFrameId = requestAnimationFrame(scrollLoop);

    return () => {
      document.removeEventListener("mousemove", onDocumentMouseMove);
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [activeIsSelecting, options?.scrollRootRef, applySelectionToIndex]);

  const getRowProps = useCallback(
    (record: T, index: number) => ({
      "data-row-index": String(index),
      "data-row-id": record._id,
      onMouseDown: (event: React.MouseEvent) => {
        const target = event.target as HTMLElement;

        if (
          !target.closest("button") &&
          !target.closest("input") &&
          !target.closest(".ant-dropdown") &&
          !target.closest(".ant-select") &&
          !target.closest("a")
        ) {
          handleMouseDown(index, event);
        }
      },
      onMouseEnter: () => {
        if (isSelecting) {
          handleMouseMove(index);
        }
      },
      onMouseMove: () => {
        if (isSelecting) {
          handleMouseMove(index);
        }
      },
      onMouseUp: (event: React.MouseEvent) => {
        if (isSelecting) {
          event.stopPropagation();
          handleMouseUp(event);
        }
      },
      style: {
        cursor: isSelecting ? "crosshair" : "default",
        userSelect: isSelecting ? "none" : "auto",
      } as React.CSSProperties,
    }),
    [handleMouseDown, handleMouseMove, handleMouseUp, isSelecting]
  );

  const rowSelection: TableProps<T>["rowSelection"] = {
    selectedRowKeys: checkedList,
    columnWidth: 30,
    renderCell: (_, record) =>
      renderTableRowCheckboxCell({
        record,
        checkedList,
        selectedRows,
        setCheckedList,
        setSelectedRows,
        checkboxClassName: options?.checkboxClassName,
      }),
    columnTitle: () =>
      renderTableRowCheckboxHeader({
        rows,
        checkedList,
        clearSelection,
        setCheckedList,
        setSelectedRows,
      }),
  };

  return {
    checkedList,
    setCheckedList,
    selectedRows,
    setSelectedRows,
    isSelecting,
    hasSelectedRows: checkedList.length > 0,
    rowSelection,
    getRowProps,
    clearSelection,
  };
}
